import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Shield } from 'lucide-react';
import type { ConsentSettings } from '../types';
import { getDefaultConsent, updateConsent } from '../utils/consent';

interface CookieConsentProps {
  onClose: () => void;
}

export default function CookieConsent({ onClose }: CookieConsentProps) {
  const { t } = useTranslation();
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<ConsentSettings>(getDefaultConsent());

  const handleAcceptAll = () => {
    const allGranted: ConsentSettings = {
      ...getDefaultConsent(),
      ad_storage: 'granted',
      analytics_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      functionality_storage: 'granted',
      personalization_storage: 'granted'
    };
    setPreferences(allGranted);
    localStorage.setItem('cookie-consent', JSON.stringify(allGranted));
    updateConsent(allGranted);
    onClose();
  };

  const handleDeclineAll = () => {
    const allDenied = getDefaultConsent();
    setPreferences(allDenied);
    localStorage.setItem('cookie-consent', JSON.stringify(allDenied));
    updateConsent(allDenied);
    onClose();
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    updateConsent(preferences);
    onClose();
  };

  const togglePreference = (key: keyof ConsentSettings) => {
    if (key === 'security_storage') return; // Cannot toggle security storage
    setPreferences(prev => ({
      ...prev,
      [key]: prev[key] === 'granted' ? 'denied' : 'granted'
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center sm:items-center z-50">
      <div className="bg-white w-full max-w-2xl rounded-t-xl sm:rounded-xl shadow-lg animate-fade-in">
        {showPreferences ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Cookie Preferences</h2>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <Shield className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium text-gray-900">Essential Cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    These cookies are necessary for the website to function and cannot be disabled.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-11 h-6 bg-blue-500 rounded-full"></div>
                </div>
              </div>

              {Object.entries(preferences).map(([key, value]) => {
                if (key === 'security_storage') return null;
                const title = {
                  ad_storage: 'Advertising Cookies',
                  analytics_storage: 'Analytics Cookies',
                  ad_user_data: 'User Data for Ads',
                  ad_personalization: 'Ad Personalization',
                  functionality_storage: 'Functionality Cookies',
                  personalization_storage: 'Personalization Cookies'
                }[key as keyof ConsentSettings];

                const description = {
                  ad_storage: 'Used to display relevant advertisements',
                  analytics_storage: 'Help us understand how you use the site',
                  ad_user_data: 'Collect data for targeted advertising',
                  ad_personalization: 'Personalize ads based on your interests',
                  functionality_storage: 'Remember your preferences',
                  personalization_storage: 'Customize your experience'
                }[key as keyof ConsentSettings];

                return (
                  <div key={key} className="flex items-start space-x-4">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{description}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => togglePreference(key as keyof ConsentSettings)}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                          ${value === 'granted' ? 'bg-blue-500' : 'bg-gray-200'}
                        `}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                            ${value === 'granted' ? 'translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex justify-end space-x-4">
              <button
                onClick={handleDeclineAll}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Decline All
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900"
              >
                Accept All
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Save Preferences
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 pr-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Your Privacy Matters
                </h2>
                <p className="text-sm text-gray-600">
                  We use cookies to enhance your experience on our site.
                  You can choose which cookies to accept. Your data will be handled in accordance
                  with our privacy policy.
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-6 flex flex-wrap gap-4">
              <button
                onClick={() => setShowPreferences(true)}
                className="flex-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Customize
              </button>
              <button
                onClick={handleDeclineAll}
                className="flex-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Decline All
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Accept All
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}